import React, { useState } from 'react';
import '../styles/CreateYourProfile.css';

const CreateYourProfile = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    summary: '',
    education: [
      {
        degree: '',
        institution: '',
        eduStartDate: '',
        eduEndDate: '',
      },
    ],
    experience: [
      {
        jobTitle: '',
        company: '',
        expStartDate: '',
        expEndDate: '',
        responsibilities: '',
      },
    ],
    professional: [
      {
        qualificationName: '',
        issuingBody: '',
        issueDate: '',
      },
    ],
    skills: [
      {
        skillType: '',
        skillList: '',
      },
    ],
  });

  const handleInputChange = (section, index, field, value) => {
    const updatedSection = [...formData[section]];
    updatedSection[index][field] = value;

    setFormData((prevData) => ({
      ...prevData,
      [section]: updatedSection,
    }));
  };

  const handlePersonalDetailsChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const addEntry = (section) => {
    const newEntry = getDefaultEntry(section);
    setFormData((prevData) => ({
      ...prevData,
      [section]: [...prevData[section], newEntry],
    }));
  };

  const removeEntry = (section, index) => {
    if (formData[section].length > 1) {
      const updatedSection = formData[section].filter((_, i) => i !== index);
      setFormData((prevData) => ({
        ...prevData,
        [section]: updatedSection,
      }));
    }
  };

  const getDefaultEntry = (section) => {
    switch (section) {
      case 'education':
        return {
          degree: '',
          institution: '',
          eduStartDate: '',
          eduEndDate: '',
        };
      case 'experience':
        return {
          jobTitle: '',
          company: '',
          expStartDate: '',
          expEndDate: '',
          responsibilities: '',
        };
      case 'professional':
        return { qualificationName: '', issuingBody: '', issueDate: '' };
      case 'skills':
        return { skillType: '', skillList: '' };
      default:
        return {};
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log('Form Data:', formData);
    alert('Form submitted! Check console for data.');
    // Here you would typically send the data to your backend
  };

  return (
    <div className='cv-form-container'>
      <h1>Create Your Profile with Fahple</h1>
      <p id='subtext'>
        Fill out the details below to create your professional profile of yours
      </p>

      <form id='cv-form' onSubmit={handleSubmit}>
        {/* Personal Details Section */}
        <div className='entry-section'>
          <h2>
            <img
              width='30'
              height='30'
              src='https://img.icons8.com/external-photo3ideastudio-solid-photo3ideastudio/64/external-personal-information-food-delivery-photo3ideastudio-solid-photo3ideastudio.png'
              alt='external-personal-information-food-delivery-photo3ideastudio-solid-photo3ideastudio'
            />
            Personal Details
          </h2>

          <label htmlFor='name'>Full Name:</label>
          <input
            type='text'
            id='name'
            name='name'
            value={formData.name}
            onChange={(e) =>
              handlePersonalDetailsChange('name', e.target.value)
            }
            required
          />

          <label htmlFor='email'>Email:</label>
          <input
            type='email'
            id='email'
            name='email'
            value={formData.email}
            onChange={(e) =>
              handlePersonalDetailsChange('email', e.target.value)
            }
            required
          />

          <label htmlFor='phone'>Phone Number:</label>
          <input
            type='tel'
            id='phone'
            name='phone'
            value={formData.phone}
            onChange={(e) =>
              handlePersonalDetailsChange('phone', e.target.value)
            }
          />

          <label htmlFor='linkedin'>LinkedIn Profile:</label>
          <input
            type='url'
            id='linkedin'
            name='linkedin'
            value={formData.linkedin}
            onChange={(e) =>
              handlePersonalDetailsChange('linkedin', e.target.value)
            }
          />

          <label htmlFor='summary'>Professional Summary:</label>
          <textarea
            id='summary'
            name='summary'
            rows='4'
            value={formData.summary}
            onChange={(e) =>
              handlePersonalDetailsChange('summary', e.target.value)
            }
          ></textarea>
        </div>

        {/* Education Section */}
        <div id='education-container'>
          {formData.education.map((edu, index) => (
            <div key={index} className='entry-section education-entry'>
              <h2>
                <img
                  width='30'
                  height='30'
                  src='https://img.icons8.com/ios/50/parse-resumes.png'
                  alt='parse-resumes'
                />
                Educational Qualifications
              </h2>

              <label htmlFor={`degree-${index}`}>Degree/Certificate:</label>
              <input
                type='text'
                id={`degree-${index}`}
                name='degree'
                value={edu.degree}
                onChange={(e) =>
                  handleInputChange(
                    'education',
                    index,
                    'degree',
                    e.target.value
                  )
                }
                required
              />

              <label htmlFor={`institution-${index}`}>Institution:</label>
              <input
                type='text'
                id={`institution-${index}`}
                name='institution'
                value={edu.institution}
                onChange={(e) =>
                  handleInputChange(
                    'education',
                    index,
                    'institution',
                    e.target.value
                  )
                }
                required
              />

              <label htmlFor={`edu-start-date-${index}`}>Start Date:</label>
              <input
                type='month'
                id={`edu-start-date-${index}`}
                name='edu-start-date'
                value={edu.eduStartDate}
                onChange={(e) =>
                  handleInputChange(
                    'education',
                    index,
                    'eduStartDate',
                    e.target.value
                  )
                }
              />

              <label htmlFor={`edu-end-date-${index}`}>End Date:</label>
              <input
                type='month'
                id={`edu-end-date-${index}`}
                name='edu-end-date'
                value={edu.eduEndDate}
                onChange={(e) =>
                  handleInputChange(
                    'education',
                    index,
                    'eduEndDate',
                    e.target.value
                  )
                }
              />

              {formData.education.length > 1 && (
                <button
                  type='button'
                  className='remove-button'
                  onClick={() => removeEntry('education', index)}
                >
                  ❌ Remove This Entry
                </button>
              )}
            </div>
          ))}
        </div>
        <button type='button' onClick={() => addEntry('education')}>
          ➕ Add Another Education
        </button>
        <hr />

        {/* Experience Section */}
        <div id='experience-container'>
          {formData.experience.map((exp, index) => (
            <div key={index} className='entry-section experience-entry'>
              <h2>
                <img
                  width='30'
                  height='30'
                  src='https://img.icons8.com/pastel-glyph/128/briefcase--v3.png'
                  alt='briefcase--v3'
                />
                Work Experience
              </h2>

              <label htmlFor={`job-title-${index}`}>Job Title:</label>
              <input
                type='text'
                id={`job-title-${index}`}
                name='job-title'
                value={exp.jobTitle}
                onChange={(e) =>
                  handleInputChange(
                    'experience',
                    index,
                    'jobTitle',
                    e.target.value
                  )
                }
                required
              />

              <label htmlFor={`company-${index}`}>Company:</label>
              <input
                type='text'
                id={`company-${index}`}
                name='company'
                value={exp.company}
                onChange={(e) =>
                  handleInputChange(
                    'experience',
                    index,
                    'company',
                    e.target.value
                  )
                }
                required
              />

              <label htmlFor={`exp-start-date-${index}`}>Start Date:</label>
              <input
                type='month'
                id={`exp-start-date-${index}`}
                name='exp-start-date'
                value={exp.expStartDate}
                onChange={(e) =>
                  handleInputChange(
                    'experience',
                    index,
                    'expStartDate',
                    e.target.value
                  )
                }
              />

              <label htmlFor={`exp-end-date-${index}`}>End Date:</label>
              <input
                type='month'
                id={`exp-end-date-${index}`}
                name='exp-end-date'
                value={exp.expEndDate}
                onChange={(e) =>
                  handleInputChange(
                    'experience',
                    index,
                    'expEndDate',
                    e.target.value
                  )
                }
              />

              <label htmlFor={`responsibilities-${index}`}>
                Key Responsibilities/Achievements:
              </label>
              <textarea
                id={`responsibilities-${index}`}
                name='responsibilities'
                rows='3'
                value={exp.responsibilities}
                onChange={(e) =>
                  handleInputChange(
                    'experience',
                    index,
                    'responsibilities',
                    e.target.value
                  )
                }
              ></textarea>

              {formData.experience.length > 1 && (
                <button
                  type='button'
                  className='remove-button'
                  onClick={() => removeEntry('experience', index)}
                >
                  ❌ Remove This Entry
                </button>
              )}
            </div>
          ))}
        </div>
        <button type='button' onClick={() => addEntry('experience')}>
          ➕ Add Another Experience
        </button>
        <hr />

        {/* Professional Qualifications Section */}
        <div id='professional-container'>
          {formData.professional.map((prof, index) => (
            <div key={index} className='entry-section professional-entry'>
              <h2>
                <img
                  width='30'
                  height='30'
                  src='https://img.icons8.com/sf-black/64/medal.png'
                  alt='medal'
                />
                Professional Qualifications
              </h2>

              <label htmlFor={`qualification-name-${index}`}>
                Qualification Name:
              </label>
              <input
                type='text'
                id={`qualification-name-${index}`}
                name='qualification-name'
                value={prof.qualificationName}
                onChange={(e) =>
                  handleInputChange(
                    'professional',
                    index,
                    'qualificationName',
                    e.target.value
                  )
                }
              />

              <label htmlFor={`issuing-body-${index}`}>Issuing Body:</label>
              <input
                type='text'
                id={`issuing-body-${index}`}
                name='issuing-body'
                value={prof.issuingBody}
                onChange={(e) =>
                  handleInputChange(
                    'professional',
                    index,
                    'issuingBody',
                    e.target.value
                  )
                }
              />

              <label htmlFor={`issue-date-${index}`}>Date of Issue:</label>
              <input
                type='month'
                id={`issue-date-${index}`}
                name='issue-date'
                value={prof.issueDate}
                onChange={(e) =>
                  handleInputChange(
                    'professional',
                    index,
                    'issueDate',
                    e.target.value
                  )
                }
              />

              {formData.professional.length > 1 && (
                <button
                  type='button'
                  className='remove-button'
                  onClick={() => removeEntry('professional', index)}
                >
                  ❌ Remove This Entry
                </button>
              )}
            </div>
          ))}
        </div>
        <button type='button' onClick={() => addEntry('professional')}>
          ➕ Add Another Qualification
        </button>
        <hr />

        {/* Skills Section */}
        <div id='skills-container'>
          {formData.skills.map((skill, index) => (
            <div key={index} className='entry-section skills-entry'>
              <h2>
                <img
                  width='30'
                  height='30'
                  src='https://img.icons8.com/ios-filled/50/problem-solving-skills.png'
                  alt='problem-solving-skills'
                />
                Skills
              </h2>

              <label htmlFor={`skill-type-${index}`}>
                Skill Category (e.g., Technical, Soft, Languages):
              </label>
              <input
                type='text'
                id={`skill-type-${index}`}
                name='skill-type'
                value={skill.skillType}
                onChange={(e) =>
                  handleInputChange(
                    'skills',
                    index,
                    'skillType',
                    e.target.value
                  )
                }
              />

              <label htmlFor={`skill-list-${index}`}>
                List of Skills (comma-separated):
              </label>
              <input
                type='text'
                id={`skill-list-${index}`}
                name='skill-list'
                value={skill.skillList}
                onChange={(e) =>
                  handleInputChange(
                    'skills',
                    index,
                    'skillList',
                    e.target.value
                  )
                }
              />

              {formData.skills.length > 1 && (
                <button
                  type='button'
                  className='remove-button'
                  onClick={() => removeEntry('skills', index)}
                >
                  ❌ Remove This Entry
                </button>
              )}
            </div>
          ))}
        </div>
        <button type='button' onClick={() => addEntry('skills')}>
          ➕ Add Another Skill Category
        </button>
        <hr />

        <button type='submit'>Submit CV Information</button>
      </form>
    </div>
  );
};

export default CreateYourProfile;
